/**
 * Server Persistence Utilities
 *
 * Manages localStorage persistence for:
 * 1. Serving server - The NVR actually hosting the dview-lite web page
 * 2. Last server - The server the user was logged into before logout
 * 3. User selection tracking - Whether user manually selected a different server
 *
 * This allows the login page to:
 * - Show which NVR is serving the page
 * - Pre-select the last server after logout for easy re-login
 * - Maintain user's server selection on page refresh
 */

import { debugLog, debugWarn } from './debug';

const SERVING_SERVER_KEY = 'dview_serving_server';
const LAST_SERVER_KEY = 'dview_last_server';
const USER_SELECTION_KEY = 'dview_user_selection'; // sessionStorage - clears on new tab

/**
 * Save the serving server (the NVR hosting this page)
 * Called on each page load to ensure it reflects the actual current URL's server
 */
export const saveServingServer = (serverInfo) => {
  if (!serverInfo || !serverInfo.bSerial) return;

  try {
    const data = {
      bSerial: serverInfo.bSerial,
      sName: serverInfo.sName,
      sIP: serverInfo.sIP || window.location.hostname,
      bPort: serverInfo.bPort || parseInt(window.location.port, 10) || 80,
      sVersion: serverInfo.sVersion,
      sReleaseVersion: serverInfo.sReleaseVersion || '',
      fReleaseCandidate: !!serverInfo.fReleaseCandidate,
      fEview: serverInfo.fEview || false
    };
    localStorage.setItem(SERVING_SERVER_KEY, JSON.stringify(data));
    debugLog('[serverPersistence] Saved serving server:', data.sName, 'fEview:', data.fEview);
  } catch (e) {
    debugWarn('[serverPersistence] Failed to save serving server:', e);
  }
};

/**
 * Get the serving server info
 */
export const getServingServer = () => {
  try {
    const data = localStorage.getItem(SERVING_SERVER_KEY);
    if (data) {
      return JSON.parse(data);
    }
  } catch (e) {
    debugWarn('[serverPersistence] Failed to load serving server:', e);
  }
  return null;
};

/**
 * Clear the serving server
 * Called on page load before fetching fresh server data
 */
export const clearServingServer = () => {
  try {
    localStorage.removeItem(SERVING_SERVER_KEY);
    debugLog('[serverPersistence] Cleared serving server');
  } catch (e) {
    debugWarn('[serverPersistence] Failed to clear serving server:', e);
  }
};

/**
 * Save user's manual server selection (sessionStorage - persists on refresh, clears on new tab)
 * This allows us to restore user's selection on refresh but default to serving server on fresh load
 */
export const saveUserSelection = (serverSerial) => {
  try {
    sessionStorage.setItem(USER_SELECTION_KEY, JSON.stringify({
      bSerial: serverSerial,
      timestamp: Date.now()
    }));
    debugLog('[serverPersistence] Saved user selection:', serverSerial);
  } catch (e) {
    debugWarn('[serverPersistence] Failed to save user selection:', e);
  }
};

/**
 * Get user's manual server selection from sessionStorage
 * Returns null if no selection or if session has expired
 */
export const getUserSelection = () => {
  try {
    const data = sessionStorage.getItem(USER_SELECTION_KEY);
    if (data) {
      const parsed = JSON.parse(data);
      // Session selections are valid for the browser session (no expiry needed)
      return parsed.bSerial;
    }
  } catch (e) {
    debugWarn('[serverPersistence] Failed to get user selection:', e);
  }
  return null;
};

/**
 * Clear user's manual server selection
 * Called on logout to reset for next session
 */
export const clearUserSelection = () => {
  try {
    sessionStorage.removeItem(USER_SELECTION_KEY);
  } catch (e) {
    debugWarn('[serverPersistence] Failed to clear user selection:', e);
  }
};

/**
 * Check if this is a fresh page load (new tab) vs a refresh
 * Uses sessionStorage which persists on refresh but clears on new tab
 */
export const isPageRefresh = () => {
  const SESSION_MARKER_KEY = 'dview_session_active';
  try {
    const marker = sessionStorage.getItem(SESSION_MARKER_KEY);
    if (marker) {
      // Session exists - this is a refresh
      return true;
    } else {
      // No session - this is a fresh load, set the marker
      sessionStorage.setItem(SESSION_MARKER_KEY, Date.now().toString());
      return false;
    }
  } catch (e) {
    return false;
  }
};

/**
 * Save the last logged-in server (for preserving on logout)
 * Called before logout to remember where the user was
 */
export const saveLastServer = (serverInfo) => {
  if (!serverInfo) return;

  try {
    const data = {
      bSerial: serverInfo.bSerial,
      sName: serverInfo.sName,
      sIP: serverInfo.sIP,
      sLocalIP: serverInfo.sLocalIP,
      bPort: serverInfo.bPort,
      sVersion: serverInfo.sVersion,
      sReleaseVersion: serverInfo.sReleaseVersion || '',
      fReleaseCandidate: !!serverInfo.fReleaseCandidate,
      fEview: serverInfo.fEview || false,
      savedAt: Date.now()
    };
    localStorage.setItem(LAST_SERVER_KEY, JSON.stringify(data));
    debugLog('[serverPersistence] Saved last server:', data.sName);
  } catch (e) {
    debugWarn('[serverPersistence] Failed to save last server:', e);
  }
};

/**
 * Get the last logged-in server
 * Returns null if no server saved or if saved more than 24 hours ago
 */
export const getLastServer = () => {
  try {
    const data = localStorage.getItem(LAST_SERVER_KEY);
    if (data) {
      const parsed = JSON.parse(data);

      // Expire after 24 hours
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours in ms
      if (parsed.savedAt && Date.now() - parsed.savedAt > maxAge) {
        debugLog('[serverPersistence] Last server expired, clearing');
        clearLastServer();
        return null;
      }

      return parsed;
    }
  } catch (e) {
    debugWarn('[serverPersistence] Failed to load last server:', e);
  }
  return null;
};

/**
 * Clear the last server (after successful login)
 */
export const clearLastServer = () => {
  try {
    localStorage.removeItem(LAST_SERVER_KEY);
  } catch (e) {
    debugWarn('[serverPersistence] Failed to clear last server:', e);
  }
};

/**
 * Check if the current page is being served by the given server
 * (Compares by serial number)
 */
export const isServingServer = (serverSerial) => {
  const serving = getServingServer();
  return serving && serving.bSerial === serverSerial;
};
